import { Skeleton, Stack, Typography } from '@mui/material';
import { useWdacResultsCount } from 'src/hooks/wdac';
import { APPLICATION_TRUST_STATUS } from 'src/utils/enums/applicationTrustStatus';
import { formatCount } from 'src/utils/formatNumber.util';
import {
  AllClearCallout,
  CardTitle,
  DETECTED_HEADING_SX,
  DetectedCallout,
  OVERVIEW_CARD_SX as CARD_SX,
  StepRow,
} from 'src/features/charts/OverviewCard';

const GET_STARTED_STEPS = [
  {
    title: 'Set a status for the apps you recognize',
    detail: 'Trust the apps that belong in your environment, straight from the table below.',
  },
  {
    title: 'Open a trusted app and build its catalog',
    detail: 'Its binaries are covered once the catalog is generated and its policy is deployed.',
  },
];

// The two states the table can be filtered down to from here, each read as a count of its own.
const NO_STATUS_QUERY = { ApplicationTrustStatuses: [APPLICATION_TRUST_STATUS.Unknown] };
// Telemetry after trust means devices still are not allowing the app, whether its catalog does not
// cover the files or its current catalog was never deployed, so the copy names both fixes.
const NEEDS_UPDATE_QUERY = {
  ApplicationTrustStatuses: [APPLICATION_TRUST_STATUS.Trusted],
  WarningRowsOnly: true,
};

const Win32AppsOverview = ({ onFiltersChange }) => {
  const noStatusQuery = useWdacResultsCount({ apiQuery: NO_STATUS_QUERY });
  const needsUpdateQuery = useWdacResultsCount({ apiQuery: NEEDS_UPDATE_QUERY });

  const noStatusCount = noStatusQuery.data ?? 0;
  const needsUpdateCount = needsUpdateQuery.data ?? 0;
  const isLoading = noStatusQuery.isLoading || needsUpdateQuery.isLoading;

  const focusNoStatus = () =>
    onFiltersChange({
      applicationTrustStatuses: [APPLICATION_TRUST_STATUS.Unknown],
      tagId: '',
      warningRowsOnly: false,
    });

  const focusNeedsUpdate = () =>
    onFiltersChange({
      applicationTrustStatuses: [APPLICATION_TRUST_STATUS.Trusted],
      tagId: '',
      warningRowsOnly: true,
    });

  return (
    <Stack gap={1.5} sx={{ width: '100%' }}>
      <Typography sx={{ color: '#FFFFFF', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1.4 }}>
        Win32 Apps Overview
      </Typography>

      <Stack
        direction={{ xs: 'column', md: 'row' }}
        gap={2}
        alignItems="stretch"
        sx={{ ...CARD_SX, width: '100%' }}>
        <Stack gap={1.25} sx={{ flex: '1 1 0', minWidth: 260 }}>
          <CardTitle title="Get Started" subtitle="Work your Win32 apps down in two steps." />
          <Stack gap={1.1}>
            {GET_STARTED_STEPS.map((step, index) => (
              <StepRow key={step.title} index={index + 1} title={step.title} detail={step.detail} />
            ))}
          </Stack>
        </Stack>

        <Stack gap={0.6} sx={{ flex: '1 1 0', minWidth: 260 }}>
          <Typography sx={DETECTED_HEADING_SX}>Detected in your environment</Typography>
          {isLoading ? (
            <Skeleton variant="rounded" height={52} sx={{ bgcolor: '#4B4B4B' }} />
          ) : (
            <>
              {!noStatusCount && !needsUpdateCount ? <AllClearCallout /> : null}
              {noStatusCount ? (
                <DetectedCallout
                  icon="mdi:shield-alert-outline"
                  accent="#E0A23C"
                  title={`${formatCount(noStatusCount)} apps without a status yet`}
                  subtitle="Start here — decide which of these belong in your environment."
                  onClick={focusNoStatus}
                />
              ) : null}
              {needsUpdateCount ? (
                <DetectedCallout
                  icon="mdi:shield-alert-outline"
                  accent="#E0A23C"
                  title={`${formatCount(needsUpdateCount)} trusted apps still reporting events`}
                  subtitle="Either update the catalog with the new files, or deploy your existing catalog."
                  onClick={focusNeedsUpdate}
                />
              ) : null}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default Win32AppsOverview;
